import { useState, useEffect, type FormEvent } from "react";
import type { KullaniciDurum, KullaniciRol } from "../../../../mocks/kullanicilar";
import { rolColors, rolIcons } from "../../../../mocks/kullanicilar";
import {
  formatPhoneTR,
  isValidPhoneTR,
} from "../../../../store/usersStore";

const ALL_ROLES: KullaniciRol[] = [
  "Admin",
  "Muhasebe",
  "Kesim Alanı",
  "Taşıyıcı",
  "Müşteri",
  "Sıra Operatörü",
  "Kesim Operatörü",
  "Parçalama Operatörü",
  "Tamamlama Operatörü",
];

const EMPLOYEE_ROLES: KullaniciRol[] = [
  "Admin",
  "Muhasebe",
  "Kesim Alanı",
  "Taşıyıcı",
  "Sıra Operatörü",
  "Kesim Operatörü",
  "Parçalama Operatörü",
  "Tamamlama Operatörü",
];

export interface UserFormData {
  adSoyad: string;
  telefon: string;
  email?: string;
  rol: KullaniciRol;
  durum: KullaniciDurum;
  sifre?: string;
  kisaNot?: string;
  forcePasswordChange: boolean;
  smsOperationalOptIn: boolean | null;
  smsMarketingOptIn: boolean | null;
}

export interface UserModalUser {
  adSoyad: string;
  telefon: string;
  email?: string;
  rol: KullaniciRol;
  durum: KullaniciDurum;
  kisaNot?: string;
  forcePasswordChange?: boolean;
  smsOperationalOptIn?: boolean | null;
  smsMarketingOptIn?: boolean | null;
}

interface Props {
  mode: "add" | "edit";
  user?: UserModalUser | null;
  onSave: (data: UserFormData) => void | Promise<void>;
  onClose: () => void;
  forceRole?: KullaniciRol;
  titleOverride?: string;
  subtitleOverride?: string;
}

const emptyForm: UserFormData = {
  adSoyad: "",
  telefon: "",
  email: "",
  rol: "Müşteri",
  durum: "Aktif",
  sifre: "Abc1234",
  kisaNot: "",
  forcePasswordChange: true,
  smsOperationalOptIn: null,
  smsMarketingOptIn: null,
};

export default function UserModal({
  mode,
  user,
  onSave,
  onClose,
  forceRole,
  titleOverride,
  subtitleOverride,
}: Props) {
  const [form, setForm] = useState<UserFormData>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (dirty) return;
    if (mode === "edit" && user) {
      setForm({
        adSoyad: user.adSoyad,
        telefon: user.telefon,
        email: user.email ?? "",
        rol: forceRole ?? user.rol,
        durum: user.durum,
        sifre: "Abc1234",
        kisaNot: user.kisaNot ?? "",
        forcePasswordChange: user.forcePasswordChange ?? false,
        smsOperationalOptIn:
          user.smsOperationalOptIn === undefined ? null : user.smsOperationalOptIn,
        smsMarketingOptIn:
          user.smsMarketingOptIn === undefined ? null : user.smsMarketingOptIn,
      });
    } else {
      setForm({
        ...emptyForm,
        rol: forceRole ?? emptyForm.rol,
      });
    }

    setErrors({});
  }, [mode, user, forceRole, dirty]);

  function setField<K extends keyof UserFormData>(key: K, value: UserFormData[K]) {
    setDirty(true);
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  }

  function validate() {
    const errs: Record<string, string> = {};

    if (!form.adSoyad.trim()) errs.adSoyad = "Ad Soyad zorunlu";
    if (!form.telefon.trim()) {
      errs.telefon = "Telefon zorunlu";
    } else if (!isValidPhoneTR(form.telefon)) {
      errs.telefon = "Geçerli bir telefon girin";
    }

    if (mode === "add" && !form.sifre?.trim()) {
      errs.sifre = "Şifre zorunlu";
    } else if (mode === "add" && form.sifre && form.sifre.trim().length < 6) {
      errs.sifre = "En az 6 karakter";
    }

    return errs;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    await onSave({
      adSoyad: form.adSoyad.trim(),
      telefon: formatPhoneTR(form.telefon),
      email: form.email?.trim() || undefined,
      rol: forceRole ?? form.rol,
      durum: form.durum,
      sifre: mode === "add" ? form.sifre?.trim() : undefined,
      kisaNot: form.kisaNot?.trim() || undefined,
      forcePasswordChange: form.forcePasswordChange,
      smsOperationalOptIn: form.smsOperationalOptIn,
      smsMarketingOptIn: form.smsMarketingOptIn,
    });

    setDirty(false);
    onClose();
  }

  const activeRole = forceRole ?? form.rol;
  const isPersonelRol = EMPLOYEE_ROLES.includes(activeRole);
  const title =
    titleOverride ?? (mode === "add" ? "Yeni Kullanıcı Ekle" : "Kullanıcıyı Düzenle");
  const subtitle =
    subtitleOverride ??
    (activeRole === "Müşteri"
      ? "Müşteri hesabı ve iletişim bilgileri"
      : "Personel hesabı ve yetki bilgileri");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-3">
      <div className="bg-farm-cream border border-farm-border rounded-farm w-full max-w-2xl shadow-lg overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-farm-border bg-farm-warm sticky top-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-farm-earth-pale text-farm-earth rounded-lg">
              <i
                className={`${
                  mode === "add" ? "ri-user-add-line" : "ri-user-settings-line"
                } text-sm`}
              />
            </div>
            <div>
              <p className="font-heading font-semibold text-sm text-farm-brown">{title}</p>
              <p className="text-[10px] text-farm-muted mt-0.5">{subtitle}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-farm-muted hover:bg-farm-border transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4">
          {!forceRole && (
            <div>
              <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1.5">
                Rol
              </label>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                {ALL_ROLES.map((r) => {
                  const rc = rolColors[r];
                  const active = form.rol === r;

                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setField("rol", r)}
                      className={`flex flex-col items-center gap-1 py-2 px-1 rounded-farm border text-[10px] font-semibold transition-colors cursor-pointer ${
                        active
                          ? `${rc.bg} ${rc.text} ${rc.border}`
                          : "bg-farm-warm border-farm-border text-farm-muted hover:border-farm-earth/30"
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${rolIcons[r]} text-sm`} />
                      </div>
                      {r}
                    </button>
                  );
                })}
              </div>

              {isPersonelRol && (
                <p className="text-[10px] text-farm-earth mt-1 flex items-center gap-1">
                  <i className="ri-information-line text-xs" />
                  Personel hesabı — sistem erişimi verilecek
                </p>
              )}
            </div>
          )}

          {forceRole && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-farm-green-pale text-farm-green border border-farm-green/20 text-[11px] font-semibold">
              <i className="ri-user-line" />
              {forceRole}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                Ad Soyad
              </label>
              <input
                value={form.adSoyad}
                onChange={(e) => setField("adSoyad", e.target.value)}
                placeholder="Adı ve soyadı girin"
                className={`w-full h-9 px-3 text-xs border rounded-farm bg-farm-warm text-farm-brown placeholder-farm-muted focus:outline-none focus:border-farm-earth ${
                  errors.adSoyad ? "border-red-400" : "border-farm-border"
                }`}
              />
              {errors.adSoyad && (
                <p className="text-[10px] text-red-500 mt-0.5">{errors.adSoyad}</p>
              )}
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                Telefon
              </label>
              <input
                value={form.telefon}
                onChange={(e) => setField("telefon", e.target.value)}
                onBlur={() => setField("telefon", formatPhoneTR(form.telefon))}
                placeholder="0532 123 45 67"
                className={`w-full h-9 px-3 text-xs font-mono border rounded-farm bg-farm-warm text-farm-brown placeholder-farm-muted focus:outline-none focus:border-farm-earth ${
                  errors.telefon ? "border-red-400" : "border-farm-border"
                }`}
              />
              {errors.telefon && (
                <p className="text-[10px] text-red-500 mt-0.5">{errors.telefon}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                E-posta <span className="normal-case font-normal">(isteğe bağlı)</span>
              </label>
              <input
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="eposta@alanadi.com"
                type="email"
                className="w-full h-9 px-3 text-xs border border-farm-border rounded-farm bg-farm-warm text-farm-brown placeholder-farm-muted focus:outline-none focus:border-farm-earth"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                Kısa Not
              </label>
              <input
                value={form.kisaNot ?? ""}
                onChange={(e) => setField("kisaNot", e.target.value)}
                placeholder="Teslimat, ödeme veya özel not"
                className="w-full h-9 px-3 text-xs border border-farm-border rounded-farm bg-farm-warm text-farm-brown placeholder-farm-muted focus:outline-none focus:border-farm-earth"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1.5">
                Durum
              </label>
              <div className="flex gap-2">
                {(["Aktif", "Pasif"] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setField("durum", d)}
                    className={`flex-1 h-9 text-xs font-semibold rounded-farm border transition-colors cursor-pointer ${
                      form.durum === d
                        ? d === "Aktif"
                          ? "bg-farm-green-pale text-farm-green border-farm-green/30"
                          : "bg-gray-100 text-gray-600 border-gray-300"
                        : "bg-farm-warm border-farm-border text-farm-muted hover:border-farm-earth/30"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {mode === "add" ? (
              <div>
                <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                  Başlangıç Şifresi
                </label>
                <input
                  value={form.sifre}
                  onChange={(e) => setField("sifre", e.target.value)}
                  type="text"
                  placeholder="En az 6 karakter"
                  className={`w-full h-9 px-3 text-xs border rounded-farm bg-farm-warm text-farm-brown placeholder-farm-muted focus:outline-none focus:border-farm-earth ${
                    errors.sifre ? "border-red-400" : "border-farm-border"
                  }`}
                />
                {errors.sifre && (
                  <p className="text-[10px] text-red-500 mt-0.5">{errors.sifre}</p>
                )}
              </div>
            ) : (
              <div className="rounded-farm border border-farm-border bg-farm-warm px-3 py-2 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-farm-brown">Kayıt Güncelleme</p>
                  <p className="text-[10px] text-farm-muted">Müşteri ve bağlı kullanıcı hesabı birlikte güncellenecek.</p>
                </div>
                <i className="ri-links-line text-farm-earth text-lg" />
              </div>
            )}
          </div>

          <div className="rounded-farm border border-farm-border bg-farm-warm p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-farm-brown">
                  İlk girişte şifre değiştir
                </p>
                <p className="text-[10px] text-farm-muted">
                  Kullanıcı ilk girişinde kendi şifresini belirlesin.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setField("forcePasswordChange", !form.forcePasswordChange)
                }
                className={`w-16 h-8 rounded-full text-[10px] font-bold border transition-colors ${
                  form.forcePasswordChange
                    ? "bg-farm-earth text-white border-farm-earth"
                    : "bg-white text-farm-muted border-farm-border"
                }`}
              >
                {form.forcePasswordChange ? "Açık" : "Kapalı"}
              </button>
            </div>

            {activeRole === "Müşteri" && (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-farm-brown">
                      Operasyonel SMS izni
                    </p>
                    <p className="text-[10px] text-farm-muted">
                      Kesim, teslimat, ödeme ve hesap akışları için.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    {[
                      { label: "Evet", value: true },
                      { label: "Hayır", value: false },
                    ].map((option) => (
                      <button
                        key={option.label}
                        type="button"
                        onClick={() =>
                          setField("smsOperationalOptIn", option.value)
                        }
                        className={`h-8 px-3 rounded-full border text-[10px] font-semibold ${
                          form.smsOperationalOptIn === option.value
                            ? option.value
                              ? "bg-farm-green-pale text-farm-green border-farm-green/30"
                              : "bg-red-50 text-red-600 border-red-200"
                            : "bg-white text-farm-muted border-farm-border"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-farm-brown">
                      Reklam / Bilgilendirme SMS izni
                    </p>
                    <p className="text-[10px] text-farm-muted">
                      Kampanya ve duyuru içerikleri için.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    {[
                      { label: "Evet", value: true },
                      { label: "Hayır", value: false },
                    ].map((option) => (
                      <button
                        key={option.label}
                        type="button"
                        onClick={() =>
                          setField("smsMarketingOptIn", option.value)
                        }
                        className={`h-8 px-3 rounded-full border text-[10px] font-semibold ${
                          form.smsMarketingOptIn === option.value
                            ? option.value
                              ? "bg-farm-green-pale text-farm-green border-farm-green/30"
                              : "bg-red-50 text-red-600 border-red-200"
                            : "bg-white text-farm-muted border-farm-border"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 rounded-full border border-farm-border bg-farm-warm text-farm-muted text-xs font-semibold hover:border-farm-earth/40"
            >
              İptal
            </button>

            <button
              type="submit"
              className="flex-1 h-10 rounded-full bg-farm-earth text-white text-xs font-semibold hover:bg-farm-brown"
            >
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
